import connect from 'components/Shared/connect';
import { NO_ELEMENT, hasElements, rm } from 'utils';
import { Faq } from './faq';
import { Faq as FaqModel } from 'models/Faq';
import { getFaqs } from './selector';
import styles from './styles.scss';
import { LoaderButton } from 'components/Button';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { getCategoryFaq, getProductFaq } from 'services/ApiFaqService';
import { useState, useEffect } from 'react';
import { getProductId } from 'views/ProductPage/selector';
import { Grid, Cell, AvailableSizes } from 'components/Grid';
import { Section } from 'components/Section';
import { isMobile } from 'components/Shared/responsive';
import cn from 'classnames';
import { getPageNumber } from 'views/ProductlistPage/selectors';
import { usePrevious, useToggle } from 'hooks';
interface OwnProps {
  title?: string;
  tabletSize: AvailableSizes;
  phoneSize: AvailableSizes;
  faqs?: FaqModel[];
  totalNumberOfFaqs?: number;
  className?: string;
}
interface MergedProps extends OwnProps {
  faqs?: FaqModel[];
  id: string;
  totalNumberOfFaqs: number;
  serviceFunction: (id: string, page: number) => Promise<FaqModel[]>;
  isMobile: boolean;
  pageNumber?: number;
}
export const FaqBlock = ({
  faqs = [],
  id,
  totalNumberOfFaqs,
  serviceFunction,
  tabletSize,
  phoneSize,
  title,
  isMobile,
  className,
  pageNumber
}: MergedProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalFaq, setFaqs] = useState(faqs);
  const [disabled, setDisabled] = useState(totalNumberOfFaqs === faqs.length);
  const prevIds = usePrevious(faqs.map(f => f.id).join(','));
  const [isLoading, toggleLoading] = useToggle(false);

  useEffect(() => {
    if (prevIds !== faqs.map(f => f.id).join(',')) {
      setFaqs(faqs);
      setCurrentPage(0);
    }
  }, [faqs, prevIds]);

  useEffect(() => {
    setDisabled(totalNumberOfFaqs === totalFaq.length);
  }, [totalFaq.length, totalNumberOfFaqs]);

  if (pageNumber && +pageNumber > 1) {
    return NO_ELEMENT;
  }

  if (!faqs || !hasElements(faqs)) {
    return NO_ELEMENT;
  }

  const loadMore = (id: string) => () => {
    toggleLoading();
    return serviceFunction(id, currentPage).then(faqs => {
      setFaqs([...totalFaq, ...faqs]);
      setCurrentPage(currentPage + 1);
      toggleLoading();
    });
  };

  const getOddOrEven = (even: boolean, faqs: FaqModel[]) => {
    const modulo = 2;
    return (
      <Cell>
        {faqs.map((item, index) => {
          if ((index % modulo !== 0 && !even) || (index % modulo === 0 && even)) {
            return <Faq {...item} key={item.id} />;
          }
          return NO_ELEMENT;
        })}
      </Cell>
    );
  };

  const getAll = (faqs: FaqModel[]) => {
    return (
      <Cell>
        {faqs.map(item => {
          return <Faq {...item} key={item.id} />;
        })}
      </Cell>
    );
  };

  return (
    <div className={cn(className, styles.faqContainer)}>
      <Section title={title} className={styles.faqSection}>
        <Grid tablet={tabletSize} phone={phoneSize} gutters="medium">
          {isMobile || tabletSize === '1' ? (
            getAll(totalFaq)
          ) : (
            <>
              {getOddOrEven(true, totalFaq)}
              {getOddOrEven(false, totalFaq)}
            </>
          )}
        </Grid>
        <Grid
          tablet={tabletSize}
          phone={phoneSize}
          gutters="hidden"
          className={styles.buttonContainer}
        >
          {!disabled && (
            <LoaderButton
              onClick={loadMore(id)}
              size="large"
              className={styles.loadMoreFaq}
              appearance="tertiary"
              loading={isLoading}
            >
              <span>{rm('product_faq_view_more_questions')}</span>
              <IconArrow appearance="flip-90" size="tiny" />
            </LoaderButton>
          )}
        </Grid>
      </Section>
    </div>
  );
};

export const CategoryFaqs = connect<MergedProps, OwnProps>(state => ({
  isMobile: isMobile(state),
  serviceFunction: getCategoryFaq,
  pageNumber: getPageNumber(state.currentPage),
  id: state.currentPage.webCatalog
}))(FaqBlock);
export const ProductFaqs = connect<MergedProps, OwnProps>(state => ({
  isMobile: isMobile(state),
  faqs: getFaqs(state),
  totalNumberOfFaqs: state.currentPage.totalNumberOfFrequentlyAskedQuestions || 0,
  id: getProductId(state.currentPage),
  serviceFunction: getProductFaq
}))(FaqBlock);
